import { useMemo } from 'react';
import type { Product } from '../../data/product';
import type { Vendor, VendorIntegration } from '../../data/vendor';

export type VendorRow = {
  vendor: Vendor;
  /**
   * Σ (stats.last_365 × last_cost) over every product attributed to this vendor
   * — a year of unit sales valued at cost.
   *
   * Note this is NOT textbook COGS: `last_cost` is the product's *current* cost
   * in LiquorPOS, not the cost at the time each unit sold, so a recent cost
   * change reprices the whole year. It is also not what was actually purchased
   * — nothing here reads receipts or invoices, so stock built up or drawn down
   * never shows.
   */
  cogs: number;
  /**
   * `cogs` ÷ the summed cogs of every vendor, 0..1. Drives both the bar's fill
   * and the percentage in its tooltip, so the two can never disagree.
   *
   * The denominator is the total across *all* vendors — including ones the
   * disabled filter is hiding, and regardless of any search — so a row's
   * percentage does not shift when the user narrows the list. Whatever is on
   * screen, "12%" means twelve percent of everything sold last year, at cost.
   */
  cogsShare: number;
  /** Most recent receipt across the vendor's products; null when nothing was ever received. */
  lastReceived: Date | null;
  features: string[];
  emails: string[];
};

export type VendorFilters = {
  query: string;
  hideDisabled: boolean;
};

export const defaultVendorFilters: VendorFilters = {
  query: '',
  hideDisabled: true,
};

type VendorStats = {
  cogs: number;
  lastReceived: Date | null;
};

/**
 * Builds one row per vendor from a single pass over `products`. The previous
 * implementation scanned the whole product array once per vendor for each of
 * two derived values — O(vendors × products) on every render.
 */
export function buildVendorRows(
  vendors: Vendor[],
  products: Product[],
  integrations: VendorIntegration[]
): VendorRow[] {
  const statsByVendorId = new Map<string, VendorStats>();
  for (const product of products) {
    let stats = statsByVendorId.get(product.vendor_id);
    if (!stats) {
      stats = { cogs: 0, lastReceived: null };
      statsByVendorId.set(product.vendor_id, stats);
    }

    stats.cogs += product.stats.last_365 * product.last_cost;

    const received = product.last_received;
    if (received && (stats.lastReceived === null || received > stats.lastReceived)) {
      stats.lastReceived = received;
    }
  }

  const integrationByName = new Map(
    integrations.map((integration) => [integration.normalized_name, integration])
  );

  // Summed over every vendor, including ones the disabled filter will hide, so
  // the percentage a row reports does not shift when a filter is toggled.
  const totalCOGS = vendors.reduce(
    (sum, vendor) => sum + (statsByVendorId.get(vendor.vendor_id)?.cogs ?? 0),
    0
  );

  return vendors.map((vendor) => {
    const stats = statsByVendorId.get(vendor.vendor_id);
    const cogs = stats?.cogs ?? 0;

    const integration = integrationByName.get(vendor.normalized_name);
    const features: string[] = [];
    if (integration?.receiving) {
      features.push('Receiving');
    }

    if (integration?.pricing) {
      features.push('Deals');
    }

    return {
      vendor,
      cogs,
      cogsShare: totalCOGS > 0 ? cogs / totalCOGS : 0,
      lastReceived: stats?.lastReceived ?? null,
      features,
      emails: (vendor.order_settings.representatives || []).map((rep) => rep.email),
    };
  });
}

/** Applies the toolbar's search box and disabled-vendor toggle. */
export function filterVendorRows(rows: VendorRow[], filters: VendorFilters): VendorRow[] {
  const query = filters.query.trim().toLowerCase();

  return rows.filter((row) => {
    if (filters.hideDisabled && row.vendor.disabled) {
      return false;
    }

    if (!query) {
      return true;
    }

    return (
      row.vendor.name.toLowerCase().includes(query) ||
      row.vendor.account_number.toLowerCase().includes(query)
    );
  });
}

export function useVendorRows(
  vendors: Vendor[],
  products: Product[],
  integrations: VendorIntegration[]
): VendorRow[] {
  return useMemo(
    () => buildVendorRows(vendors, products, integrations),
    [vendors, products, integrations]
  );
}
